import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { ShieldCheck, ExternalLink, RefreshCw, ArrowLeft, X, Key, Loader2, LogOut, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { useModuleStore } from '@/stores/module-store';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { useContainerSize } from '@/hooks/use-container-size';
import { signOut } from '@/lib/firebase/auth';

// Zoho datacenter regions for the dc selector
const ZOHO_DC_OPTIONS = [
  { value: 'US', label: 'United States (.com)' },
  { value: 'EU', label: 'Europe (.eu)' },
  { value: 'IN', label: 'India (.in)' },
  { value: 'AU', label: 'Australia (.com.au)' },
  { value: 'JP', label: 'Japan (.jp)' },
  { value: 'CA', label: 'Canada (.ca)' },
  { value: 'SA', label: 'Saudi Arabia (.sa)' },
  { value: 'CN', label: 'China (.com.cn)' },
];

export function SaaSAuthPrompt() {
  const saasModule = useModuleStore((s) => s.saasModule);
  const saasAuthenticated = useModuleStore((s) => s.saasAuthenticated);
  const saasAuthChecking = useModuleStore((s) => s.saasAuthChecking);
  const saasAuthChecked = useModuleStore((s) => s.saasAuthChecked);
  const saasAuthError = useModuleStore((s) => s.saasAuthError);
  const saasAccessError = useModuleStore((s) => s.saasAccessError);
  const checkSaaSAuth = useModuleStore((s) => s.checkSaaSAuth);
  const initiateSaaSAuth = useModuleStore((s) => s.initiateSaaSAuth);
  const initiateSaaSNonAdminAuth = useModuleStore((s) => s.initiateSaaSNonAdminAuth);
  const getSaaSAuthUrl = useModuleStore((s) => s.getSaaSAuthUrl);
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const userEmail = useAuthStore((s) => s.user?.email);

  // LookUp running as a panel injected into a host page (Chrome extension
  // mounts us as `#lookup-handleFrame-<saas>` and passes the id through).
  const isEmbeddedLookupPanel = useMemo(() => {
    if (appCodeTPA !== 'lookup') return false;
    return !!new URLSearchParams(window.location.search).get('iframeId');
  }, [appCodeTPA]);

  /**
   * This prompt covers the whole panel, so while the CRM is unauthorized there
   * is nothing usable behind it — re-checking auth just brings the prompt
   * straight back, which reads as the panel reloading itself. In that case
   * "close" has to mean close the panel: ask the host to unmount it. The host
   * also drops the app from its config, so it stays closed until the user
   * re-enables it. Everywhere else the button keeps its original behaviour of
   * dismissing the prompt by re-checking auth.
   */
  const handleClose = useCallback(() => {
    if (isEmbeddedLookupPanel && appCodeSAAS) {
      window.parent?.postMessage({ type: 'removeLookupApp', app: appCodeSAAS }, '*');
      return;
    }
    checkSaaSAuth();
  }, [isEmbeddedLookupPanel, appCodeSAAS, checkSaaSAuth]);

  // Datacenter region state — default to US
  const [selectedDC, setSelectedDC] = useState('US');
  // Monday.com subdomain state (e.g. "mycompany" or "mycompany.monday.com")
  const [mondaySubdomain, setMondaySubdomain] = useState('');
  // Bitrix24 domain state (e.g. "mycompany.bitrix24.com")
  const [bitrix24Domain, setBitrix24Domain] = useState('');
  // Track whether user has clicked Authorize (show post-auth screen)
  const [authInitiated, setAuthInitiated] = useState(false);
  // Store the auth URL for "Open in new tab" fallback
  const [authUrl, setAuthUrl] = useState<string | null>(null);

  // API key auth state
  const [apiKeyDomain, setApiKeyDomain] = useState('');
  const [apiKeyValue, setApiKeyValue] = useState('');
  const [apiKeySaving, setApiKeySaving] = useState(false);
  const [apiKeyError, setApiKeyError] = useState('');
  // Resolved API key help URL (from SDK or static config)
  const [apiKeyHelpUrl, setApiKeyHelpUrl] = useState<string | null>(null);

  // OAuth domain state — for modules that need domain before OAuth (e.g. Zendesk Support)
  const [oauthDomain, setOauthDomain] = useState('');
  const [oauthDomainError, setOauthDomainError] = useState('');

  // Detect if this is a Zoho SaaS module (show dc selector only for Zoho)
  const isZoho = appCodeSAAS?.toLowerCase().startsWith('zoho') ?? false;
  // Detect if this is a Monday.com module (show subdomain input)
  const isMonday = appCodeSAAS?.toLowerCase().includes('monday') ?? false;
  // Detect if this is a Bitrix24 module (show domain input)
  const isBitrix24 = appCodeSAAS?.toLowerCase().includes('bitrix24') ?? false;

  const { isMini } = useContainerSize();

  // Measure auth dialog content and resize Pipedrive iframe to fit
  const authCardRef = useRef<HTMLDivElement>(null);
  const resizePipedrive = useCallback(() => {
    const el = authCardRef.current;
    const saas = useModuleStore.getState().saasModule;
    if (!el || !saas?.resizeToHeight) return;
    let contentHeight = 0;
    for (const child of Array.from(el.children)) {
      contentHeight += (child as HTMLElement).offsetHeight;
    }
    saas.resizeToHeight(contentHeight + 100);
  }, []);

  useEffect(() => {
    const el = authCardRef.current;
    if (!el) return;
    resizePipedrive();
    const observer = new ResizeObserver(() => resizePipedrive());
    observer.observe(el);
    return () => observer.disconnect();
  }, [resizePipedrive, authInitiated, saasAuthChecked, saasAuthenticated]);

  // Detect if module supports non-admin auth (e.g. HubSpot)
  const hasNonAdminAuth = !!saasModule?.initiateNonAdminAuth;

  // Detect if module uses API key auth (e.g. Freshdesk, Freshworks)
  const isApiKeyAuth = saasModule?.authType === 'apikey';
  const apiKeyConfig = saasModule?.apiKeyConfig;

  // Detect if module requires domain input before OAuth (e.g. Zendesk Support)
  const oauthDomainConfig = saasModule?.oauthDomainConfig;

  // Resolve API key help URL when domain changes — try SDK-based method, fallback to static config
  useEffect(() => {
    const domain = apiKeyDomain.trim();
    if (!isApiKeyAuth || !domain) {
      setApiKeyHelpUrl(null);
      return;
    }

    let cancelled = false;
    const cleanDomain = domain.replace(/^https?:\/\//, '').replace(/\/+$/, '');

    // Try dynamic SDK-based URL first (e.g. Freshdesk uses SDK to get user profile ID)
    if (saasModule?.getApiKeyHelpUrl) {
      saasModule.getApiKeyHelpUrl(cleanDomain).then((url) => {
        if (cancelled) return;
        if (url) {
          setApiKeyHelpUrl(url);
        } else if (apiKeyConfig?.apiKeyHelpUrl) {
          // Fallback to static template
          setApiKeyHelpUrl(apiKeyConfig.apiKeyHelpUrl.replace('{{domain}}', cleanDomain));
        } else {
          setApiKeyHelpUrl(null);
        }
      });
    } else if (apiKeyConfig?.apiKeyHelpUrl) {
      // No dynamic method — use static template
      setApiKeyHelpUrl(apiKeyConfig.apiKeyHelpUrl.replace('{{domain}}', cleanDomain));
    } else {
      setApiKeyHelpUrl(null);
    }

    return () => { cancelled = true; };
  }, [apiKeyDomain, isApiKeyAuth, saasModule, apiKeyConfig]);

  // Listen for OAuth completion message from popup
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type !== 'oauth-complete' || !event.data?.success) return;
      const ok = await checkSaaSAuth();
      // Same completion as the "I've authorized" button: prod's
      // authorization-success popup reloads the opener, but that call is
      // cross-origin (throws) when the app runs on localhost, so this
      // listener has to finish the flow there. Without the reload the
      // session keeps the old org in memory while re-auth already dropped
      // its cached org id — stale sync toggle + webhook-owner banner.
      if (ok) window.location.reload();
    };

    const handleReauthRequired = () => {
      // An arbitrary API call 401'd — but that could be a scope issue, a
      // permission problem on a specific record, or a transient server hiccup.
      // Re-verify with the current user's own `getCurrentUser` endpoint before
      // surfacing the auth prompt. If that still fails, checkSaaSAuth will
      // flip `saasAuthenticated` to false and the prompt will render.
      if (useModuleStore.getState().saasAuthChecking) return;
      checkSaaSAuth();
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('zoho:reauth-required', handleReauthRequired);
    window.addEventListener('saas:reauth-required', handleReauthRequired);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('zoho:reauth-required', handleReauthRequired);
      window.removeEventListener('saas:reauth-required', handleReauthRequired);
    };
  }, [checkSaaSAuth]);

  // Don't show until getCurrentUser has been attempted and failed
  if (!saasModule || saasAuthenticated || !saasAuthChecked) {
    return null;
  }

  const saasName = saasModule.name || appCodeSAAS || 'your service';
  const tpaName = tpaModule?.name || 'your messaging provider';

  // Non-auth access error — admin hasn't granted API access for this profile.
  // Re-authorization won't fix it, so replace the OAuth button with a
  // help-article link (matches legacy zoho-crm-things.js behavior).
  if (saasAccessError === 'NO_PERMISSION') {
    const helpUrl = 'https://help.zoho.com/portal/en/community/topic/organization-api-code-403-crm-implied-api-access-error-for-https-www-zohoapis-com-crm-v2-org';
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
        <div
          ref={authCardRef}
          className="w-full max-w-md rounded-xl border border-border bg-card shadow-lg p-6 space-y-5"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15">
              <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0">
              <h2 className="font-display text-base font-semibold text-foreground">
                {saasName} API access is disabled
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {userEmail ? `for ${userEmail}` : 'for this profile'}
              </p>
            </div>
          </div>

          <div className="text-sm text-muted-foreground space-y-3">
            <p>
              Your {saasName} admin needs to enable <span className="font-medium text-foreground">"Zoho CRM API Access"</span> on
              your profile before {tpaName} can read or write records. Signing
              in again will not fix this.
            </p>
            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <p className="text-xs font-medium text-foreground mb-2">Steps for the admin:</p>
              <ol className="list-decimal list-outside pl-4 space-y-1 text-xs">
                <li>Open {saasName} and click the <span className="font-medium text-foreground">Setup</span> (gear) icon.</li>
                <li>Go to <span className="font-medium text-foreground">Security Control → Profiles</span>.</li>
                <li>Select the profile assigned to this user.</li>
                <li>Scroll to the bottom — under <span className="font-medium text-foreground">Developer Permissions</span>, toggle on <span className="font-medium text-foreground">Zoho CRM API Access</span> and save.</li>
                <li>Come back here and hit <span className="font-medium text-foreground">Retry</span>.</li>
              </ol>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              onClick={() => window.open(helpUrl, '_blank', 'noopener,noreferrer')}
              className="w-full"
            >
              <ExternalLink className="h-4 w-4" />
              View on community
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                useModuleStore.getState().setSaasAccessError(null);
                checkSaaSAuth();
              }}
              disabled={saasAuthChecking}
              className="w-full"
            >
              {saasAuthChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              I've enabled it — retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleAuthorize = () => {
    // If module needs a domain for OAuth, validate and extract subdomain
    let domain: string | undefined;
    if (oauthDomainConfig) {
      let domainValue = oauthDomain.trim();
      if (!domainValue) {
        setOauthDomainError(`Please enter your ${oauthDomainConfig.label || 'domain'}`);
        return;
      }
      // Strip protocol and trailing slashes
      domainValue = domainValue.replace(/^https?:\/\//, '').replace(/\/+$/, '');
      if (!domainValue) {
        setOauthDomainError('Please enter a valid domain');
        return;
      }
      // Some modules need the full domain (e.g. WooCommerce store URL, Bitrix24)
      // Others need just the subdomain (e.g. Zendesk "mycompany" → {{DOMAIN}}.zendesk.com)
      if (oauthDomainConfig.useFullDomain) {
        domain = domainValue;
      } else {
        // Extract subdomain (e.g. "mycompany" from "mycompany.zendesk.com" or just "mycompany")
        domain = domainValue.split('.')[0];
      }
      if (!domain) {
        setOauthDomainError('Please enter a valid domain');
        return;
      }
      setOauthDomainError('');
    }

    const region = isZoho ? selectedDC : undefined;
    const url = initiateSaaSAuth(region, domain);
    setAuthUrl(url ?? getSaaSAuthUrl(region, domain));
    setAuthInitiated(true);
  };

  const handleOpenInNewTab = () => {
    if (authUrl) {
      window.open(authUrl, '_blank');
    }
  };

  const handleCheckAuth = async () => {
    const ok = await checkSaaSAuth();
    // Mirror the prod flow: the authorization-success popup reloads the app
    // (opener.location.reload()) after auth completes — but that reload is
    // cross-origin (throws) when the app runs on localhost, so this button is
    // how the flow finishes there. Reload on success so the app re-initializes
    // against the (possibly different) authorized org, exactly like prod.
    if (ok) window.location.reload();
  };

  const handleBack = () => {
    setAuthInitiated(false);
    setAuthUrl(null);
  };

  const handleNonAdminAuthorize = () => {
    initiateSaaSNonAdminAuth();
    setAuthInitiated(true);
  };

  // API key auth — save credentials
  const handleApiKeySave = async () => {
    if (!apiKeyValue.trim()) {
      setApiKeyError('Please enter your API Key');
      return;
    }
    // Strip protocol and trailing slashes from domain
    let domain = apiKeyDomain.trim().replace(/^https?:\/\//, '').replace(/\/+$/, '');
    if (!domain) {
      setApiKeyError('Please enter your domain');
      return;
    }
    setApiKeySaving(true);
    setApiKeyError('');
    try {
      if (saasModule.saveApiKeyCredentials) {
        await saasModule.saveApiKeyCredentials(apiKeyValue.trim(), domain);
      }
      const ok = await checkSaaSAuth();
      // Explicit completion point — reload like the OAuth paths do, so the
      // app re-initializes against the (possibly different) org instead of
      // running on the previous org's in-memory context.
      if (ok) {
        window.location.reload();
        return;
      }
      setApiKeySaving(false);
      setApiKeyError('Credentials saved but verification failed. Please check and try again.');
    } catch (err) {
      console.error('[SaaSAuthPrompt] Error saving API key:', err);
      setApiKeyError('Failed to save credentials. Please try again.');
      setApiKeySaving(false);
    }
  };

  // Inline logo element for headings
  const logoInline = saasModule.icon ? (
    <img
      src={saasModule.icon}
      alt={saasName}
      className="inline-block max-h-6 max-w-[140px] object-contain"
    />
  ) : (
    <span>{saasName}</span>
  );

  return (
    <div data-testid="saas-auth-prompt" className={
      isMini
        ? "fixed inset-0 z-[60] flex flex-col bg-background overflow-y-auto"
        : "fixed inset-0 z-[60] flex items-center justify-center bg-background/80 backdrop-blur-sm"
    }>
      <div
        ref={authCardRef}
        className={
          isMini
            ? "relative w-full flex-1 flex flex-col bg-card px-5 pt-[20%] pb-6"
            : "relative w-full max-w-md rounded-lg border border-border bg-card p-8 shadow-lg"
        }
      >
        {/* Close — closes the panel when embedded, else re-checks auth */}
        <button
          onClick={handleClose}
          title={isEmbeddedLookupPanel ? 'Close' : 'Dismiss'}
          aria-label={isEmbeddedLookupPanel ? 'Close' : 'Dismiss'}
          className="absolute top-3 right-3 rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className={isMini ? "mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10" : "mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10"}>
            <ShieldCheck className={isMini ? "h-5 w-5 text-primary" : "h-6 w-6 text-primary"} />
          </div>

          {isApiKeyAuth ? (
            /* ── API Key auth screen: domain + API key form ── */
            <>
              <h2 className={isMini ? "mb-1 flex items-center gap-2 text-base font-semibold text-foreground" : "mb-1 flex items-center gap-2 text-lg font-semibold text-foreground"}>
                Authorize {logoInline}
              </h2>

              <p className={isMini ? "mb-3 text-xs text-muted-foreground" : "mb-5 text-sm text-muted-foreground"}>
                Enter your {apiKeyConfig?.serviceName || saasName} domain and API key to proceed.
              </p>

              {(saasAuthError || apiKeyError) && (
                <div className="mb-4 w-full rounded-md bg-destructive/10 p-2.5 text-xs text-destructive">
                  {apiKeyError || saasAuthError}
                </div>
              )}

              <div className="w-full space-y-4 text-left">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    {apiKeyConfig?.serviceName || saasName} Domain
                  </label>
                  <input
                    type="text"
                    value={apiKeyDomain}
                    onChange={(e) => setApiKeyDomain(e.target.value)}
                    placeholder={apiKeyConfig?.domainPlaceholder || `e.g. mycompany${apiKeyConfig?.domainSuffix || '.com'}`}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    disabled={apiKeySaving}
                  />
                </div>

                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className="block text-xs font-medium text-muted-foreground">
                      {apiKeyConfig?.serviceName || saasName} API Key
                    </label>
                    {apiKeyHelpUrl && (
                      <a
                        href={apiKeyHelpUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        Get API Key
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <input
                    type="text"
                    value={apiKeyValue}
                    onChange={(e) => setApiKeyValue(e.target.value)}
                    placeholder="Enter your API Key"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    disabled={apiKeySaving}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleApiKeySave();
                    }}
                  />
                </div>

                <Button
                  onClick={handleApiKeySave}
                  disabled={apiKeySaving}
                  className="w-full"
                >
                  {apiKeySaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Authorizing...
                    </>
                  ) : (
                    <>
                      <Key className="mr-2 h-4 w-4" />
                      Authorize Now
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : !authInitiated ? (
            /* ── Initial screen: select dc + authorize (OAuth) ── */
            <>
              <h2 className={isMini ? "mb-1 flex items-center gap-2 text-base font-semibold text-foreground" : "mb-1 flex items-center gap-2 text-lg font-semibold text-foreground"}>
                Authorize {logoInline}
              </h2>

              <p className={isMini ? "mb-3 text-xs text-muted-foreground" : "mb-5 text-sm text-muted-foreground"}>
                To use {tpaName} integration.
              </p>

              {(saasAuthError || oauthDomainError) && (
                <div className="mb-4 w-full rounded-md bg-destructive/10 p-2.5 text-xs text-destructive">
                  {oauthDomainError || saasAuthError}
                </div>
              )}

              {/* Zoho datacenter selector */}
              {isZoho && (
                <div className="mb-4 w-full text-left">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Datacenter Region
                  </label>
                  <Select
                    value={selectedDC}
                    onValueChange={setSelectedDC}
                    options={ZOHO_DC_OPTIONS}
                    placeholder="Select datacenter..."
                  />
                </div>
              )}

              {/* OAuth domain input — for modules that need domain before OAuth (e.g. Zendesk Support, Monday.com, Bitrix24) */}
              {oauthDomainConfig && (
                <div className="mb-4 w-full text-left">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    {oauthDomainConfig.label}
                  </label>
                  <input
                    type="text"
                    value={oauthDomain}
                    onChange={(e) => { setOauthDomain(e.target.value); setOauthDomainError(''); }}
                    placeholder={`e.g. ${oauthDomainConfig.domainPlaceholder}`}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAuthorize();
                    }}
                  />
                </div>
              )}

              <Button
                onClick={handleAuthorize}
                disabled={saasAuthChecking || (oauthDomainConfig && !oauthDomain.trim())}
                className="w-full"
              >
                {saasAuthChecking ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Authorize Now
                  </>
                )}
              </Button>

              {/* Non-admin auth option (HubSpot) */}
              {hasNonAdminAuth && (
                <div className="mt-4 w-full">
                  <button
                    onClick={handleNonAdminAuthorize}
                    disabled={saasAuthChecking}
                    className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-2"
                  >
                    Not an admin? <span className="underline font-medium">Authorize as non-admin</span>
                  </button>
                </div>
              )}
            </>
          ) : (
            /* ── Post-auth screen: waiting for user to complete auth (OAuth) ── */
            <>
              <h2 className={isMini ? "mb-1 flex items-center gap-2 text-base font-semibold text-foreground" : "mb-1 flex items-center gap-2 text-lg font-semibold text-foreground"}>
                Waiting for {logoInline}
              </h2>

              <p className={isMini ? "mb-3 text-xs text-muted-foreground" : "mb-5 text-sm text-muted-foreground"}>
                Complete the authorization in the popup window.
                If the popup didn&apos;t open, use the link below.
              </p>

              {saasAuthError && (
                <div className="mb-4 w-full rounded-md bg-destructive/10 p-2.5 text-xs text-destructive">
                  {saasAuthError}
                </div>
              )}

              <div className="flex w-full flex-col gap-3">
                {/* Open in new tab fallback */}
                {authUrl && (
                  <Button
                    variant="outline"
                    onClick={handleOpenInNewTab}
                    className="w-full"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open in New Tab
                  </Button>
                )}

                {/* Check authorization */}
                <Button
                  onClick={handleCheckAuth}
                  disabled={saasAuthChecking}
                  className="w-full"
                >
                  {saasAuthChecking ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      I&apos;ve Authorized — Refresh
                    </>
                  )}
                </Button>

                {/* Go back */}
                <button
                  onClick={handleBack}
                  className="mt-1 flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-3 w-3" />
                  Back
                </button>
              </div>
            </>
          )}
        </div>

        {/* Sign out */}
        <div className={isMini ? "mt-3 pt-2 border-t border-border" : "mt-5 pt-3 border-t border-border"}>
          <button
            onClick={() => signOut()}
            className="flex items-center justify-center gap-1.5 w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out ({userEmail})
          </button>
        </div>
      </div>
    </div>
  );
}
